<!-- Post Creator Component - Clean design -->
<div class="post-creator-card bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
  <!-- Header section with avatar and textarea -->
  <div class="flex items-start gap-3 sm:gap-4">
    <img
      [src]="userAvatar"
      [alt]="userName || 'Tu foto'"
      class="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover border border-gray-100"
    />

    <div class="flex-1">
      <textarea
        #textArea
        [(ngModel)]="newPostContent"
        (ngModelChange)="onContentChanged()"
        (input)="autoGrowTextArea($event)"
        [placeholder]="placeholder || '¿Qué estás pensando?'"
        class="expanding-textarea w-full bg-transparent border-none resize-none focus:ring-0 outline-none text-gray-800 placeholder-gray-500 text-base sm:text-lg leading-relaxed min-h-[88px] sm:min-h-[96px] max-h-[280px] overflow-hidden p-0"
        rows="3"
        [disabled]="isLoading"
        (focus)="onTextAreaFocus()"
        (blur)="onTextAreaBlur()"
      >
      </textarea>
      <div
        *ngIf="validationMessage"
        class="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
      >
        <i class="fas fa-info-circle mt-0.5 text-amber-500"></i>
        <span>{{ validationMessage }}</span>
      </div>
    </div>
  </div>

  <!-- File upload hidden inputs -->
  <input
    type="file"
    #imageInput
    (change)="onFileSelected($event)"
    multiple
    accept="image/*"
    class="hidden"
  />
  <input
    type="file"
    #videoInput
    (change)="onFileSelected($event)"
    multiple
    accept="video/*"
    class="hidden"
  />
  <input
    type="file"
    #documentInput
    (change)="onFileSelected($event)"
    multiple
    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
    class="hidden"
  />

  <!-- Selected files preview -->
  <div class="creator-offset mt-3" *ngIf="selectedFiles.length > 0">
    <div class="flex flex-wrap gap-2">
      <div
        *ngFor="let file of selectedFiles; let i = index"
        class="flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-sm font-medium"
      >
        <i [class]="getFileIcon(file)"></i>
        <span class="truncate max-w-[150px]">{{ file.name }}</span>
        <button
          type="button"
          (click)="removeFile(i)"
          class="text-blue-400 hover:text-blue-600 ml-1 focus:outline-none"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>
  </div>

  <!-- Tags section -->
  <div class="creator-offset mt-3" *ngIf="showTagInput || tags.length > 0">
    <div class="flex flex-wrap gap-2 mb-2" *ngIf="tags.length > 0">
      <span
        *ngFor="let tag of tags; let i = index"
        class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700"
      >
        #{{ tag }}
        <button
          type="button"
          (click)="removeTag(i)"
          class="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-600 focus:outline-none"
        >
          <span class="sr-only">Remove tag</span>
          <svg class="w-2 h-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
            <path stroke-linecap="round" stroke-width="1.5" d="M1 1l6 6m0-6L1 7" />
          </svg>
        </button>
      </span>
    </div>
    <div class="flex items-center border-b border-gray-200 pb-1" *ngIf="showTagInput">
      <span class="text-gray-400 mr-2">#</span>
      <input
        type="text"
        [(ngModel)]="newTag"
        (keydown.enter)="addTag($event)"
        (keydown.space)="addTag($event)"
        placeholder="Añadir etiquetas (presiona Enter o Espacio)"
        class="flex-1 bg-transparent border-none focus:ring-0 text-sm text-gray-700 placeholder-gray-400 outline-none p-0"
        [disabled]="isLoading"
      />
      <button
        *ngIf="newTag"
        (click)="addTag()"
        class="text-blue-600 hover:text-blue-800 text-sm font-medium px-2"
      >
        Añadir
      </button>
    </div>
  </div>

  <!-- Poll creator section -->
  <div
    class="creator-offset mt-3 p-3 sm:p-4 border border-gray-100 rounded-lg bg-gray-50/50"
    *ngIf="showPollCreator"
  >
    <div class="flex items-center justify-between mb-3">
      <h4 class="font-medium text-gray-800 text-sm">Crear Encuesta</h4>
      <button (click)="cancelPoll()" class="text-gray-400 hover:text-gray-600">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <div class="space-y-3">
      <input
        [(ngModel)]="pollQuestion"
        placeholder="Escribe tu pregunta..."
        class="w-full p-2.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm transition-all"
        [disabled]="isLoading"
      />

      <div class="space-y-2">
        <div
          *ngFor="let option of pollOptions; let i = index; trackBy: trackByIndex"
          class="flex items-center gap-2"
        >
          <input
            [value]="pollOptions[i]"
            (input)="onPollOptionInput($event, i)"
            [placeholder]="'Opción ' + (i + 1)"
            class="flex-1 p-2.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm transition-all"
            [disabled]="isLoading"
            [id]="'poll-option-' + i"
          />
          <button
            *ngIf="pollOptions.length > 2"
            (click)="removePollOption(i)"
            class="text-gray-400 hover:text-red-500 p-2 transition-colors"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <button
        *ngIf="pollOptions.length < 10"
        (click)="addPollOption()"
        class="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1.5 transition-colors"
      >
        <i class="fas fa-plus"></i>
        Agregar opción
      </button>

      <!-- Validación visual de la encuesta -->
      <div class="mt-3 text-xs" *ngIf="showPollCreator">
        <div class="flex items-center gap-1.5 text-gray-500">
          <i class="fas fa-info-circle"></i>
          <span>Se necesita una pregunta y al menos 2 opciones válidas.</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Bottom section with options and post button -->
  <div
    class="border-t border-gray-100 mt-4 pt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
  >
    <!-- Left side options -->
    <div class="flex flex-wrap items-center gap-1" *ngIf="showOptions">
      <button
        class="p-2 text-blue-500 hover:bg-blue-50 rounded-full transition-colors disabled:opacity-50"
        (click)="onOptionClick('photo')"
        [disabled]="isLoading"
        title="Agregar fotos"
      >
        <i class="fas fa-image text-lg"></i>
      </button>

      <button
        class="p-2 text-green-500 hover:bg-green-50 rounded-full transition-colors disabled:opacity-50"
        (click)="onOptionClick('video')"
        [disabled]="isLoading"
        title="Agregar videos"
      >
        <i class="fas fa-video text-lg"></i>
      </button>

      <button
        class="p-2 text-purple-500 hover:bg-purple-50 rounded-full transition-colors disabled:opacity-50"
        (click)="onOptionClick('file')"
        [disabled]="isLoading"
        title="Agregar archivos"
      >
        <i class="fas fa-file-alt text-lg"></i>
      </button>

      <button
        class="p-2 text-orange-500 hover:bg-orange-50 rounded-full transition-colors disabled:opacity-50"
        (click)="onOptionClick('poll')"
        [disabled]="isLoading"
        title="Crear encuesta"
      >
        <i class="fas fa-poll text-lg"></i>
      </button>

      <button
        class="p-2 text-pink-500 hover:bg-pink-50 rounded-full transition-colors disabled:opacity-50"
        (click)="onOptionClick('tag')"
        [disabled]="isLoading"
        title="Añadir etiquetas"
      >
        <i class="fas fa-hashtag text-lg"></i>
      </button>
    </div>

    <!-- Right side post button -->
    <button
      class="w-full sm:w-auto justify-center px-5 py-2 sm:py-1.5 bg-blue-600 text-white font-medium rounded-full hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center gap-2"
      (click)="submitPost()"
      [disabled]="isLoading"
    >
      <span *ngIf="!isLoading">{{ buttonText || 'Publicar' }}</span>
      <i *ngIf="isLoading" class="fas fa-spinner fa-spin"></i>
    </button>
  </div>
</div>
